import React, { useEffect, useRef, useState } from 'react';

const BRAND_BLUE = '#2365ab';
const DEEP_BLUE = '#163d6b';
const SOFT_BLUE = '#dfeeff';
const SKY_BLUE = '#5f97dd';

const scopePoints = [
  'Reúne en una sola pantalla todos los chats de WhatsApp de los pacientes para leerlos, organizarlos y responderlos sin salir del sistema.',
  'Muestra el historial completo de cada conversación: textos, audios, imágenes, PDF y contactos compartidos.',
  'Trabaja junto a Anita, la asistente virtual: ella atiende automáticamente y vos podés tomar la charla cuando haga falta una persona.',
  'Ayuda a priorizar: de un vistazo ves lo que está sin atender, lo que tiene mensajes nuevos y lo que ya quedó resuelto.',
  'Mantiene el orden del día sin perder ningún mensaje del paciente ni mezclar conversaciones.'
];

const screenParts = [
  {
    title: 'Lista de chats (izquierda)',
    body: 'Es el listado de todas las conversaciones. Cada fila muestra el nombre del paciente, una vista previa del último mensaje, la hora y un aviso cuando hay mensajes sin leer.'
  },
  {
    title: 'Conversación (centro)',
    body: 'Al elegir un chat se abre la charla completa. Acá leés los mensajes, reproducís audios, abrís imágenes o PDF y escribís tu respuesta.'
  },
  {
    title: 'Ficha del paciente (derecha)',
    body: 'Muestra los datos de contacto y el contexto del paciente. Te ayuda a saber con quién estás hablando antes de responder.'
  }
];

const filterCards = [
  { title: 'Todos', body: 'Muestra todas las conversaciones, sin importar su estado. Ideal para una mirada general de la jornada.' },
  { title: 'No atendidos', body: 'Chats en curso que todavía no fueron finalizados. Es la cola de trabajo que está esperando una respuesta.' },
  { title: 'No leídos', body: 'Conversaciones con mensajes nuevos del paciente que aún nadie abrió. Son los más urgentes de revisar.' },
  { title: 'Resueltos', body: 'Casos ya finalizados o cerrados. Sirven para consultar el historial cuando lo necesites.' }
];

const organizeTools = [
  { title: 'Búsqueda', body: 'Escribí un nombre o un número de teléfono para encontrar al instante la conversación que buscás.' },
  { title: 'Filtro por operador', body: 'Mostrá solo los chats de un operador en particular, para enfocarte en lo tuyo o revisar el trabajo de alguien.' },
  { title: 'Paginación', body: 'La lista se divide en páginas. Usá los controles para avanzar, retroceder o volver al inicio sin que se ponga lenta.' },
  { title: 'Actualización automática', body: 'La lista se refresca sola para traer los mensajes nuevos. Un pequeño aviso te indica cuando se está actualizando.' }
];

const readingFeatures = [
  'Mensajes de texto del paciente y de los operadores, ordenados por hora.',
  'Audios con reproductor integrado: play, pausa y barra de progreso para escucharlos sin descargar nada.',
  'Imágenes y archivos PDF que se abren con un clic, también en una pestaña nueva si lo preferís.',
  'Contactos compartidos (.vcf) que el paciente envía desde su teléfono.',
  'Botón para cargar mensajes anteriores y un atajo para volver rápido al final de la charla.'
];

const anitaSteps = [
  {
    title: 'Anita atiende sola',
    body: 'Por defecto, la asistente virtual Anita responde automáticamente al paciente y va avanzando la gestión sin que tengas que intervenir.'
  },
  {
    title: 'Tomás la charla',
    body: 'Cuando hace falta una persona, pausás a Anita y tomás vos la conversación. A partir de ese momento el paciente habla directamente con el operador.'
  },
  {
    title: 'Devolvés al asistente',
    body: 'Cuando terminás de ayudar, devolvés la conversación a Anita para que siga atendiendo de forma automática. El historial queda guardado.'
  }
];

const respondSteps = [
  'Abrí, desde la lista, el chat al que querés responder.',
  'Si Anita está atendiendo, primero tomá la charla para poder escribir.',
  'Redactá tu mensaje en el cuadro de texto de la parte de abajo.',
  'Si hace falta, adjuntá una imagen, un audio, un PDF o un contacto.',
  'Enviá el mensaje: le llega al paciente por WhatsApp al instante.',
  'Cuando el caso quede resuelto, finalizá o devolvé la charla a Anita.'
];

const attachmentTypes = [
  { title: 'Imagen', body: 'Fotos o capturas para mostrarle algo concreto al paciente.' },
  { title: 'Audio', body: 'Notas de voz cuando es más rápido o claro explicar hablando.' },
  { title: 'PDF', body: 'Documentos como instructivos, comprobantes o información formal.' },
  { title: 'Contacto (.vcf)', body: 'Compartir un número de contacto cuando corresponde derivar al paciente.' }
];

const permissionCards = [
  {
    title: 'Ver Conversaciones',
    body: 'Para entrar a la pantalla, la cuenta necesita el permiso de ver conversaciones. Si no lo tiene, el módulo no aparece en el menú lateral.'
  },
  {
    title: 'Responder y tomar la charla',
    body: 'Tomar el chat y responderle al paciente son acciones reservadas a cuentas habilitadas. Otros perfiles pueden mirar la conversación, pero no operar sobre ella.'
  },
  {
    title: 'Aviso de mensajes nuevos',
    body: 'Quienes tienen acceso ven un aviso flotante con la cantidad de mensajes sin leer, para no perderse ningún contacto del paciente.'
  }
];

const rolePermissions = [
  {
    title: 'Super admin',
    body: 'Acceso total: ve todas las conversaciones, toma cualquier chat, responde y administra qué puede hacer cada uno del resto del equipo.'
  },
  {
    title: 'Administrativo',
    body: 'Según los permisos que tenga asignados, entra a Conversaciones para dar seguimiento. La posibilidad de responder directo depende de su habilitación vigente.'
  },
  {
    title: 'Médico',
    body: 'Su trabajo principal está en Pedidos/Recetas. Normalmente no opera Conversaciones, salvo que se le habilite expresamente.'
  }
];

const bestPractices = [
  'Revisá primero los filtros “No leídos” y “No atendidos” para no dejar pacientes esperando.',
  'Antes de responder, mirá la ficha del paciente y leé el contexto de la charla.',
  'Si tomás una conversación, resolvela o devolvela a Anita; no la dejes a mitad de camino.',
  'Usá audios e imágenes solo cuando sumen claridad; preferí mensajes breves y directos.',
  'Verificá que estás en el chat del paciente correcto antes de enviar.',
  'Finalizá los casos resueltos para mantener la lista ordenada y enfocada.'
];

const faqItems = [
  {
    title: '¿Por qué no veo Conversaciones en el menú?',
    body: 'Porque tu cuenta no tiene el permiso para ver conversaciones. Pedíselo al administrador para que te lo habilite.'
  },
  {
    title: '¿Puedo escribirle al paciente si Anita está atendiendo?',
    body: 'Primero tenés que tomar la charla, es decir, pausar a Anita. Recién ahí se habilita el cuadro para responder vos.'
  },
  {
    title: '¿Qué pasa cuando devuelvo la charla a Anita?',
    body: 'Anita retoma la atención automática y todo el historial de la conversación queda guardado tal cual.'
  },
  {
    title: '¿Se pierden los chats viejos?',
    body: 'No. Quedan disponibles: podés buscarlos, usar el filtro “Resueltos” y cargar los mensajes anteriores dentro de cada charla.'
  },
  {
    title: '¿Por qué veo el chat pero no puedo responder?',
    body: 'Ver y responder son permisos distintos. Responder requiere una cuenta habilitada para operar sobre la conversación.'
  }
];

const manualPages = [
  { id: 'portada', label: 'Portada', eyebrow: 'Manual', title: 'Manual de Conversaciones' },
  { id: 'que-es', label: 'Qué es', eyebrow: 'Panorama', title: 'Qué es Conversaciones' },
  { id: 'pantalla', label: 'Pantalla', eyebrow: 'Interfaz', title: 'La pantalla de un vistazo' },
  { id: 'buscar', label: 'Organizar', eyebrow: 'Orden', title: 'Buscar y filtrar chats' },
  { id: 'leer', label: 'Leer', eyebrow: 'Mensajes', title: 'Leer una conversación' },
  { id: 'anita', label: 'Anita', eyebrow: 'Asistente', title: 'Anita y el operador humano' },
  { id: 'responder', label: 'Responder', eyebrow: 'Acción', title: 'Responder y enviar' },
  { id: 'permisos', label: 'Permisos', eyebrow: 'Accesos', title: 'Permisos y roles' },
  { id: 'practicas', label: 'Prácticas', eyebrow: 'Calidad', title: 'Buenas prácticas' },
  { id: 'faq', label: 'FAQ', eyebrow: 'Cierre', title: 'Preguntas frecuentes' }
];

export default function AdminManualConversaciones() {
  const [viewportWidth, setViewportWidth] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth : 1280
  ));
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [turnDirection, setTurnDirection] = useState('next');
  const manualContentRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = viewportWidth < 640;
  const isTablet = viewportWidth < 980;
  const isSinglePageView = isTablet;
  const styles = buildStyles({ isMobile, isTablet });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const styleId = 'manual-book-animations';
    let styleTag = document.getElementById(styleId);
    let created = false;

    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      created = true;
    }

    styleTag.textContent = `
      @keyframes manualBookTurnNext {
        0% { opacity: 0; transform: translateX(20px) rotateY(-7deg) scale(0.985); }
        100% { opacity: 1; transform: translateX(0) rotateY(0deg) scale(1); }
      }
      @keyframes manualBookTurnPrev {
        0% { opacity: 0; transform: translateX(-20px) rotateY(7deg) scale(0.985); }
        100% { opacity: 1; transform: translateX(0) rotateY(0deg) scale(1); }
      }
    `;

    if (created) document.head.appendChild(styleTag);

    return () => {
      if (created && styleTag?.parentNode) styleTag.parentNode.removeChild(styleTag);
    };
  }, []);

  const pagesPerView = isSinglePageView ? 1 : 2;
  const maxPageIndex = Math.max(0, manualPages.length - pagesPerView);

  useEffect(() => {
    setActivePageIndex((prev) => {
      if (isSinglePageView) return Math.min(prev, manualPages.length - 1);
      const normalized = Math.min(prev, Math.max(0, manualPages.length - 2));
      return normalized % 2 === 0 ? normalized : normalized - 1;
    });
  }, [isSinglePageView]);

  const goToPage = (index) => {
    const normalizedTarget = isSinglePageView ? index : index - (index % 2);
    const clamped = Math.max(0, Math.min(normalizedTarget, maxPageIndex));
    setTurnDirection(clamped >= activePageIndex ? 'next' : 'prev');
    setActivePageIndex(clamped);
  };

  const goNextSpread = () => {
    if (activePageIndex >= maxPageIndex) return;
    setTurnDirection('next');
    setActivePageIndex((prev) => Math.min(prev + pagesPerView, maxPageIndex));
  };

  const goPrevSpread = () => {
    if (activePageIndex <= 0) return;
    setTurnDirection('prev');
    setActivePageIndex((prev) => Math.max(prev - pagesPerView, 0));
  };

  const visiblePages = manualPages.slice(activePageIndex, activePageIndex + pagesPerView);
  const spreadCount = Math.ceil(manualPages.length / pagesPerView);
  const currentSpread = Math.floor(activePageIndex / pagesPerView) + 1;

  const handleExportPdf = async () => {
    if (!manualContentRef.current || isExportingPdf || typeof window === 'undefined') return;

    const previewWindow = window.open('', '_blank');

    try {
      setIsExportingPdf(true);

      if (previewWindow) {
        previewWindow.document.write(`
          <html>
            <head>
              <title>Manual de Conversaciones - PDF A4</title>
              <style>
                html, body {
                  margin: 0;
                  height: 100%;
                  background: #0f172a;
                  font-family: Arial, sans-serif;
                }
                .manual-pdf-loading {
                  height: 100%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: #e2e8f0;
                  letter-spacing: 0.02em;
                }
              </style>
            </head>
            <body>
              <div class="manual-pdf-loading">Generando vista previa del PDF...</div>
            </body>
          </html>
        `);
        previewWindow.document.close();
      }

      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const exportPages = Array.from(
        manualContentRef.current.querySelectorAll('[data-export-manual-page="true"]')
      );

      for (let index = 0; index < exportPages.length; index += 1) {
        const exportPage = exportPages[index];
        const canvas = await html2canvas(exportPage, {
          scale: Math.max(2, window.devicePixelRatio || 1),
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: exportPage.scrollWidth,
          windowHeight: exportPage.scrollHeight
        });

        const imgData = canvas.toDataURL('image/png');

        if (index > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
      }

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      if (!previewWindow) {
        pdf.save('manual-conversaciones-a4.pdf');
      } else {
        previewWindow.document.open();
        previewWindow.document.write(`
          <html>
            <head>
              <title>Manual de Conversaciones - PDF A4</title>
              <style>
                html, body {
                  margin: 0;
                  height: 100%;
                  background: #111827;
                }
                iframe {
                  border: 0;
                  width: 100%;
                  height: 100%;
                  display: block;
                  background: #111827;
                }
              </style>
            </head>
            <body>
              <iframe src="${pdfUrl}" title="Manual de Conversaciones - PDF A4"></iframe>
            </body>
          </html>
        `);
        previewWindow.document.close();
        window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
      }
    } catch (error) {
      console.error('Error exportando PDF del manual:', error);
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <div style={{
      flex: 1,
      minHeight: '100%',
      overflowY: 'visible',
      overflowX: 'hidden',
      background: 'radial-gradient(circle at top left, rgba(35, 101, 171, 0.22), transparent 30%), radial-gradient(circle at top right, rgba(95, 151, 221, 0.16), transparent 26%), linear-gradient(180deg, #f8fbff 0%, #edf4ff 100%)',
      padding: isMobile ? '16px 12px 112px' : '28px clamp(18px, 3vw, 38px) 124px'
    }}>
      <div style={styles.bookShell}>
        <div style={styles.topControlRow}>
          <div style={styles.chapterDeck}>
            <div style={styles.chapterDeckHeader}>
              <div>
                <div style={styles.chapterDeckKicker}>Edición navegable</div>
                <div style={styles.chapterDeckTitle}>Capítulos del manual</div>
              </div>
              <div style={styles.spreadCounter}>Spread {String(currentSpread).padStart(2, '0')} / {String(spreadCount).padStart(2, '0')}</div>
            </div>
            <div style={styles.chapterRailWrap}>
              <div style={styles.chapterRailFadeLeft} />
              <div style={styles.chapterRail}>
                {manualPages.map((page, index) => {
                  const isActive = visiblePages.some((visiblePage) => visiblePage.id === page.id);
                  return (
                    <button
                      key={page.id}
                      type="button"
                      onClick={() => goToPage(index)}
                      style={{
                        ...styles.chapterPill,
                        ...(isActive ? styles.chapterPillActive : null)
                      }}
                    >
                      <span style={styles.chapterPillNumber}>{String(index + 1).padStart(2, '0')}</span>
                      <span>{page.label}</span>
                    </button>
                  );
                })}
              </div>
              <div style={styles.chapterRailFadeRight} />
            </div>
          </div>
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={isExportingPdf}
            style={styles.pdfActionButton}
          >
            {isExportingPdf ? 'Generando PDF...' : 'Exportar PDF'}
          </button>
        </div>

        <div style={styles.bookStage}>
          <div style={styles.bookAura} />
          {!isSinglePageView && <div style={styles.bookSpine} />}
          <div
            key={`${activePageIndex}-${pagesPerView}`}
            style={{
              ...styles.bookSpread,
              gridTemplateColumns: isSinglePageView ? '1fr' : '1fr 1fr',
              animationName: turnDirection === 'next' ? 'manualBookTurnNext' : 'manualBookTurnPrev'
            }}
          >
            {visiblePages.map((page, index) => (
              <article key={page.id} style={{ ...styles.bookPage, ...(page.id === 'portada' ? styles.bookPageCover : null), ...(index === 0 && !isSinglePageView ? styles.bookPageLeft : styles.bookPageRight) }}>
                <div style={page.id === 'portada' ? styles.pageInnerGlowCover : styles.pageInnerGlow} />
                {page.id !== 'portada' && !isMobile && <div style={styles.pageCornerFold} />}
                {page.id !== 'portada' && (
                  <>
                    <div style={styles.pageChromeTop}>
                      <span style={styles.pageEyebrow}>{page.eyebrow}</span>
                      <span style={styles.pageNumber}>{String(activePageIndex + index + 1).padStart(2, '0')}</span>
                    </div>
                    <h2 style={styles.pageTitle}>{page.title}</h2>
                  </>
                )}
                <div style={{ ...styles.pageBody, ...(page.id === 'portada' ? styles.pageBodyCover : null) }}>
                  {renderManualPage(page.id, styles, { isMobile, isTablet, forExport: false })}
                </div>
              </article>
            ))}
          </div>
        </div>

        <div style={styles.bookNavRow}>
          <button type="button" onClick={goPrevSpread} disabled={activePageIndex <= 0} style={{ ...styles.bookNavButton, ...(activePageIndex <= 0 ? styles.bookNavButtonDisabled : null) }}>
            <span style={styles.bookNavArrow}>←</span>
            <span style={styles.bookNavButtonText}>Hoja anterior</span>
          </button>
          <div style={styles.bookNavHint}>Navegación por spreads y capítulos</div>
          <button type="button" onClick={goNextSpread} disabled={activePageIndex >= maxPageIndex} style={{ ...styles.bookNavButton, ...(activePageIndex >= maxPageIndex ? styles.bookNavButtonDisabled : null) }}>
            <span style={styles.bookNavButtonText}>Hoja siguiente</span>
            <span style={styles.bookNavArrow}>→</span>
          </button>
        </div>

      </div>

      <div ref={manualContentRef} style={styles.exportRoot}>
        {manualPages.map((page, index) => (
          <section key={page.id} data-export-manual-page="true" style={{ ...styles.exportPage, ...(page.id === 'portada' ? styles.exportPageCover : null) }}>
            {page.id !== 'portada' && (
              <>
                <div style={styles.exportPageHeader}>
                  <span style={styles.exportEyebrow}>{page.eyebrow}</span>
                  <span style={styles.exportPageNumber}>{String(index + 1).padStart(2, '0')}</span>
                </div>
                <h2 style={styles.exportPageTitle}>{page.title}</h2>
              </>
            )}
            {renderManualPage(page.id, styles, { isMobile: false, isTablet: false, forExport: true })}
          </section>
        ))}
      </div>
    </div>
  );
}

function renderManualPage(pageId, styles, { isMobile, isTablet, forExport }) {
  const compactCardStyle = {
    ...styles.infoCard,
    padding: forExport ? 18 : styles.infoCard.padding,
    boxShadow: forExport ? 'none' : styles.infoCard.boxShadow
  };

  switch (pageId) {
    case 'portada':
      if (forExport) {
        return (
          <div style={styles.exportCoverShell}>
            <div style={styles.exportCoverOverline}>Manual operativo interno</div>
            <div style={styles.exportCoverBrand}>Grupo Bienestar</div>
            <h1 style={styles.exportCoverTitle}>Manual de Conversaciones</h1>
            <p style={styles.exportCoverText}>
              Guía simple y visual para usar la pantalla de Conversaciones: leer chats de WhatsApp, organizarlos, trabajar junto a Anita y responderle al paciente.
            </p>
            <div style={styles.exportCoverDivider} />
            <div style={styles.exportCoverFooter}>
              <span style={styles.exportCoverFooterItem}>Para el equipo</span>
              <span style={styles.exportCoverFooterItem}>Libro interactivo</span>
              <span style={styles.exportCoverFooterItem}>WhatsApp + Anita</span>
            </div>
          </div>
        );
      }

      return (
        <div style={styles.coverPageShell}>
          <div style={styles.coverPageGlow} />
          <div style={styles.coverPageTopline}>Manual operativo interno</div>
          <div style={styles.coverPageBrand}>Grupo Bienestar</div>
          <h1 style={styles.coverPageTitle}>Manual de Conversaciones</h1>
          <p style={styles.coverPageText}>
            Una guía simple y visual para usar la pantalla de Conversaciones: leer los chats de WhatsApp, mantenerlos ordenados, trabajar junto a la asistente Anita y responderle al paciente.
          </p>
          <div style={styles.coverPageEditionRow}>
            <span style={styles.coverPageEditionChip}>Edición 2026</span>
            <span style={styles.coverPageEditionChip}>{manualPages.length} capítulos</span>
            <span style={styles.coverPageEditionChip}>Para el usuario final</span>
          </div>
          <div style={styles.coverPageDivider} />
          <div style={styles.coverPageMetaGrid}>
            <div style={styles.coverPageMetaCard}>
              <div style={styles.coverPageMetaLabel}>Pensado para</div>
              <div style={styles.coverPageMetaValue}>Operadores</div>
            </div>
            <div style={styles.coverPageMetaCard}>
              <div style={styles.coverPageMetaLabel}>Formato</div>
              <div style={styles.coverPageMetaValue}>Libro interactivo</div>
            </div>
            <div style={styles.coverPageMetaCard}>
              <div style={styles.coverPageMetaLabel}>Incluye</div>
              <div style={styles.coverPageMetaValue}>Permisos y roles</div>
            </div>
          </div>
        </div>
      );
    case 'que-es':
      return (
        <div style={styles.pageStack}>
          <div style={styles.coverLeadCard}>
            <div style={styles.coverLeadLabel}>De qué se trata</div>
            <p style={styles.coverLeadText}>
              Conversaciones es la pantalla donde el equipo atiende los chats de WhatsApp de los pacientes. Todo lo que el
              paciente escribe, graba o envía llega a un mismo lugar, ordenado y fácil de seguir.
            </p>
            <p style={{ ...styles.coverLeadText, marginBottom: 0 }}>
              No estás solo: la asistente virtual Anita responde de forma automática y vos podés sumarte cuando haga falta
              una persona, sin perder el contexto de la charla.
            </p>
          </div>
          <div style={styles.pageStackCompact}>
            {scopePoints.map((item) => (
              <div key={item} style={styles.noteRowSoft}>
                <span style={styles.noteAccentSoft} />
                <span style={{ color: '#1e293b', lineHeight: 1.65 }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );
    case 'pantalla':
      return (
        <div style={styles.pageStack}>
          <div style={styles.grid3}>
            {screenParts.map((card) => (
              <article key={card.title} style={compactCardStyle}>
                <h3 style={styles.cardTitle}>{card.title}</h3>
                <p style={{ ...styles.copy, marginBottom: 0 }}>{card.body}</p>
              </article>
            ))}
          </div>
          <article style={styles.highlightPanelAlt}>
            <div style={styles.highlightTitle}>En resumen</div>
            <p style={{ ...styles.copy, marginBottom: 0 }}>
              A la izquierda elegís el chat, en el centro lo leés y respondés, y a la derecha ves quién es el paciente.
              Esa misma lógica se repite en todas las conversaciones.
            </p>
          </article>
        </div>
      );
    case 'buscar':
      return (
        <div style={styles.pageStack}>
          <div style={styles.grid2}>
            {filterCards.map((card) => (
              <article key={card.title} style={compactCardStyle}>
                <div style={styles.cardTag}>{card.title}</div>
                <p style={{ ...styles.copy, marginBottom: 0 }}>{card.body}</p>
              </article>
            ))}
          </div>
          <div style={styles.grid2}>
            {organizeTools.map((card) => (
              <article key={card.title} style={compactCardStyle}>
                <h3 style={styles.cardTitle}>{card.title}</h3>
                <p style={{ ...styles.copy, marginBottom: 0 }}>{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      );
    case 'leer':
      return (
        <div style={styles.pageStack}>
          <div style={styles.pageStackCompact}>
            {readingFeatures.map((item) => (
              <div key={item} style={styles.noteRow}>
                <span style={styles.noteAccent} />
                <span style={{ color: '#0f172a', lineHeight: 1.6 }}>{item}</span>
              </div>
            ))}
          </div>
          <article style={styles.highlightPanel}>
            <div style={styles.highlightTitle}>Buen dato</div>
            <p style={{ ...styles.highlightPanelText, marginBottom: 0 }}>
              Si una charla es larga, usá el botón para cargar mensajes anteriores y el atajo para volver al final.
              Así nunca perdés de vista lo último que escribió el paciente.
            </p>
          </article>
        </div>
      );
    case 'anita':
      return (
        <div style={styles.pageStack}>
          <div style={styles.pageStackCompact}>
            {anitaSteps.map((step, index) => (
              <article key={step.title} style={styles.flowCard}>
                <div style={styles.flowIndex}>{index + 1}</div>
                <div>
                  <h3 style={styles.cardTitle}>{step.title}</h3>
                  <p style={{ ...styles.copy, marginBottom: 0 }}>{step.body}</p>
                </div>
              </article>
            ))}
          </div>
          <article style={styles.highlightPanelAlt}>
            <div style={styles.highlightTitle}>Lo importante</div>
            <p style={{ ...styles.copy, marginBottom: 0 }}>
              Mientras Anita atiende, el paciente recibe respuestas automáticas. Cuando tomás la charla, las respuestas
              pasan a ser tuyas. Acordate de devolvérsela a Anita al terminar.
            </p>
          </article>
        </div>
      );
    case 'responder':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Paso a paso para responder</h3>
            <ol style={styles.orderedList}>
              {respondSteps.map((step) => <li key={step}>{step}</li>)}
            </ol>
          </article>
          <div style={styles.grid2}>
            {attachmentTypes.map((card) => (
              <article key={card.title} style={compactCardStyle}>
                <h3 style={styles.cardTitle}>{card.title}</h3>
                <p style={{ ...styles.copy, marginBottom: 0 }}>{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      );
    case 'permisos':
      return (
        <div style={styles.pageStack}>
          <div style={styles.grid3}>
            {permissionCards.map((card) => (
              <article key={card.title} style={compactCardStyle}>
                <h3 style={styles.cardTitle}>{card.title}</h3>
                <p style={{ ...styles.copy, marginBottom: 0 }}>{card.body}</p>
              </article>
            ))}
          </div>
          <div style={styles.grid3}>
            {rolePermissions.map((card) => (
              <article key={card.title} style={compactCardStyle}>
                <div style={styles.cardTag}>{card.title}</div>
                <p style={{ ...styles.copy, marginBottom: 0 }}>{card.body}</p>
              </article>
            ))}
          </div>
          <article style={styles.highlightPanel}>
            <div style={styles.highlightTitle}>Ver no es lo mismo que responder</div>
            <p style={{ ...styles.highlightPanelText, marginBottom: 0 }}>
              Una cuenta puede tener permiso para ver las conversaciones y, aun así, no poder responderle al paciente.
              Responder y tomar la charla son acciones reservadas a cuentas habilitadas.
            </p>
          </article>
        </div>
      );
    case 'practicas':
      return (
        <div style={styles.pageStack}>
          <div style={styles.pageStackCompact}>
            {bestPractices.map((item) => (
              <div key={item} style={styles.noteRowSoft}>
                <span style={styles.noteAccentSoft} />
                <span style={{ color: '#1e293b', lineHeight: 1.65 }}>{item}</span>
              </div>
            ))}
          </div>
          <article style={styles.coverLeadCard}>
            <div style={styles.coverLeadLabel}>La idea de fondo</div>
            <p style={{ ...styles.coverLeadText, marginBottom: 0 }}>
              Una buena atención es rápida, clara y ordenada. Si dejás cada chat resuelto o devuelto a Anita, el equipo
              entero trabaja mejor y ningún paciente queda sin respuesta.
            </p>
          </article>
        </div>
      );
    case 'faq':
      return (
        <div style={styles.pageStack}>
          <div style={styles.pageStackCompact}>
            {faqItems.map((item) => (
              <article key={item.title} style={styles.faqCard}>
                <h3 style={styles.cardTitle}>{item.title}</h3>
                <p style={{ ...styles.copy, marginBottom: 0 }}>{item.body}</p>
              </article>
            ))}
          </div>
          <article style={styles.coverLeadCard}>
            <div style={styles.coverLeadLabel}>Cierre</div>
            <p style={{ ...styles.coverLeadText, marginBottom: 0 }}>
              Este manual refleja la pantalla de Conversaciones tal como funciona hoy. Si la herramienta cambia, conviene
              actualizar estas páginas para que el equipo siempre tenga la guía al día.
            </p>
          </article>
        </div>
      );
    default:
      return null;
  }
}

function buildStyles({ isMobile, isTablet }) {
  return {
  bookShell: {
    maxWidth: 1380,
    margin: '0 auto',
    display: 'grid',
    gridTemplateRows: 'auto auto auto auto',
    gap: isMobile ? 14 : isTablet ? 18 : 24
  },
  topControlRow: {
    display: 'grid',
    gridTemplateColumns: isTablet ? '1fr' : '1fr auto',
    gap: isMobile ? 10 : 12,
    alignItems: 'center'
  },
  chapterDeck: {
    display: 'grid',
    gap: 8,
    minWidth: 0,
    padding: isMobile ? '10px 10px 8px' : '12px 12px 10px',
    borderRadius: isMobile ? 20 : 24,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(241,246,255,0.98) 100%)',
    border: '1px solid rgba(184, 204, 232, 0.9)',
    boxShadow: '0 18px 36px rgba(35, 101, 171, 0.08)',
    backdropFilter: 'blur(16px)'
  },
  chapterDeckHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: isMobile ? 'wrap' : 'nowrap'
  },
  chapterDeckKicker: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: '#6a86a8',
    marginBottom: 3
  },
  chapterDeckTitle: {
    fontSize: isMobile ? 14 : 15,
    fontWeight: 800,
    color: '#102a46'
  },
  heroBanner: {
    display: 'grid',
    gridTemplateColumns: isTablet ? '1fr' : '1.2fr 0.8fr',
    gap: isMobile ? 12 : 14,
    alignItems: 'stretch',
    borderRadius: isMobile ? 24 : 32,
    padding: isMobile ? '16px 14px' : '18px 22px',
    background: `linear-gradient(135deg, ${DEEP_BLUE} 0%, ${BRAND_BLUE} 58%, ${SKY_BLUE} 150%)`,
    boxShadow: '0 26px 90px rgba(22, 61, 107, 0.22)',
    color: '#f8fbff',
    position: 'relative',
    overflow: 'hidden'
  },
  heroCopy: {
    position: 'relative',
    zIndex: 1
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '5px 11px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.16)',
    border: '1px solid rgba(255,255,255,0.2)',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase'
  },
  heroTitle: {
    margin: '10px 0 8px',
    fontSize: isMobile ? 'clamp(24px, 7vw, 30px)' : 'clamp(30px, 3.6vw, 42px)',
    lineHeight: 1.04
  },
  heroText: {
    margin: 0,
    fontSize: isMobile ? 13 : 14,
    lineHeight: 1.55,
    color: 'rgba(248,251,255,0.92)',
    maxWidth: 620
  },
  heroActions: {
    display: 'grid',
    alignContent: 'center',
    gap: 10,
    justifyItems: isTablet ? 'stretch' : 'end',
    width: '100%'
  },
  heroActionButton: {
    border: '1px solid rgba(255,255,255,0.24)',
    background: 'rgba(255,255,255,0.14)',
    color: '#f8fafc',
    borderRadius: 999,
    padding: isMobile ? '9px 12px' : '10px 14px',
    fontSize: isMobile ? 12 : 13,
    fontWeight: 800,
    letterSpacing: '0.02em',
    cursor: 'pointer',
    boxShadow: '0 14px 28px rgba(9, 30, 66, 0.18)',
    backdropFilter: 'blur(10px)',
    width: isTablet ? '100%' : 'auto',
    textAlign: 'center'
  },
  spreadCounter: {
    padding: '8px 12px',
    borderRadius: 14,
    background: 'linear-gradient(135deg, rgba(16, 42, 70, 0.96) 0%, rgba(35, 101, 171, 0.92) 100%)',
    border: '1px solid rgba(165, 205, 248, 0.26)',
    fontSize: 12,
    fontWeight: 700,
    color: '#dbeafe',
    boxShadow: '0 12px 24px rgba(16, 42, 70, 0.2)'
  },
  chapterRailWrap: {
    position: 'relative',
    minWidth: 0
  },
  chapterRailFadeLeft: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 24,
    background: 'linear-gradient(90deg, rgba(245,249,255,1) 0%, rgba(245,249,255,0) 100%)',
    pointerEvents: 'none',
    zIndex: -1,
    borderRadius: '18px 0 0 18px'
  },
  chapterRailFadeRight: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 24,
    background: 'linear-gradient(270deg, rgba(245,249,255,1) 0%, rgba(245,249,255,0) 100%)',
    pointerEvents: 'none',
    zIndex: 1,
    borderRadius: '0 18px 18px 0'
  },
  chapterRail: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 6,
    minWidth: 0,
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingLeft: 2,
    paddingRight: 10,
    paddingBottom: 2,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none'
  },
  pdfActionButton: {
    border: '1px solid rgba(16, 56, 96, 0.14)',
    background: '#ffffff',
    color: DEEP_BLUE,
    borderRadius: 999,
    padding: isMobile ? '10px 12px' : '10px 16px',
    fontSize: isMobile ? 12 : 13,
    fontWeight: 800,
    letterSpacing: '0.01em',
    cursor: 'pointer',
    boxShadow: '0 12px 24px rgba(35, 101, 171, 0.08)',
    width: isTablet ? '100%' : 'auto',
    justifySelf: isTablet ? 'stretch' : 'end'
  },
  chapterPill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexShrink: 0,
    gap: 6,
    padding: isMobile ? '6px 8px' : '6px 9px',
    borderRadius: 999,
    border: '1px solid rgba(184, 204, 232, 0.9)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(241,246,255,0.92) 100%)',
    color: '#28486f',
    fontSize: isMobile ? 10 : 11,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 12px 24px rgba(35, 101, 171, 0.08)',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  },
  chapterPillActive: {
    background: `linear-gradient(135deg, ${DEEP_BLUE} 0%, ${BRAND_BLUE} 100%)`,
    color: '#f8fbff',
    borderColor: 'rgba(22, 61, 107, 0.9)',
    boxShadow: '0 16px 34px rgba(22, 61, 107, 0.18)'
  },
  chapterPillNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: isMobile ? 16 : 18,
    height: isMobile ? 16 : 18,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.18)',
    fontSize: isMobile ? 8 : 9,
    fontWeight: 900
  },
  bookStage: {
    position: 'relative',
    padding: isMobile ? '4px 0 0' : isTablet ? '8px 0 0' : '12px 12px 0',
    perspective: 1800,
    isolation: 'isolate'
  },
  bookAura: {
    position: 'absolute',
    inset: '20px 5% auto',
    height: isMobile ? 120 : 180,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(35, 101, 171, 0.14) 0%, rgba(35, 101, 171, 0) 72%)',
    filter: 'blur(10px)'
  },
  bookSpine: {
    position: 'absolute',
    left: '50%',
    top: 22,
    bottom: 12,
    width: 34,
    transform: 'translateX(-50%)',
    borderRadius: 999,
    background: 'linear-gradient(180deg, rgba(8, 28, 52, 0.96) 0%, rgba(18, 57, 97, 0.98) 18%, rgba(35, 101, 171, 0.92) 52%, rgba(18, 57, 97, 0.98) 82%, rgba(8, 28, 52, 0.96) 100%)',
    border: '1px solid rgba(165, 205, 248, 0.22)',
    boxShadow: '0 12px 34px rgba(12, 41, 78, 0.24), inset 0 1px 0 rgba(255,255,255,0.16), inset 0 0 0 1px rgba(10, 29, 54, 0.24)',
    zIndex: 0
  },
  bookSpread: {
    position: 'relative',
    display: 'grid',
    gap: isMobile ? 14 : 18,
    alignItems: 'stretch',
    animationDuration: '420ms',
    animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
    animationFillMode: 'both'
  },
  bookPage: {
    position: 'relative',
    minHeight: isMobile ? '60vh' : isTablet ? '66vh' : '76vh',
    maxHeight: isMobile ? '68vh' : isTablet ? '72vh' : '78vh',
    overflow: 'hidden',
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr',
    gap: 12,
    padding: isMobile ? '16px 14px' : isTablet ? '18px 18px 16px' : '22px 22px 18px',
    borderRadius: isMobile ? 24 : 28,
    background: 'linear-gradient(180deg, #ffffff 0%, #fcfdff 45%, #f3f7ff 100%)',
    border: '1px solid rgba(191, 208, 232, 0.95)',
    boxShadow: '0 30px 60px rgba(15, 23, 42, 0.12), inset 0 1px 0 rgba(255,255,255,0.9)',
    zIndex: 1
  },
  pageInnerGlow: {
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 28%), linear-gradient(180deg, rgba(35,101,171,0.04) 0%, rgba(35,101,171,0) 16%, rgba(15,23,42,0.03) 100%)',
    pointerEvents: 'none',
    zIndex: 0
  },
  pageInnerGlowCover: {
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0) 26%), linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 22%, rgba(8,28,52,0.14) 100%)',
    pointerEvents: 'none',
    zIndex: 0
  },
  pageCornerFold: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 62,
    height: 62,
    background: 'linear-gradient(135deg, rgba(223,238,255,0) 0%, rgba(223,238,255,0) 49%, rgba(223,238,255,0.98) 50%, rgba(198,220,247,0.98) 100%)',
    clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
    opacity: 0.9,
    pointerEvents: 'none',
    zIndex: 0
  },
  bookPageCover: {
    background: `linear-gradient(160deg, ${DEEP_BLUE} 0%, ${BRAND_BLUE} 52%, ${SKY_BLUE} 100%)`,
    border: '1px solid rgba(143, 187, 236, 0.34)',
    boxShadow: '0 34px 72px rgba(10, 35, 66, 0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
    color: '#f8fbff'
  },
  bookPageLeft: {
    transformOrigin: 'right center'
  },
  bookPageRight: {
    transformOrigin: 'left center'
  },
  pageChromeTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: isMobile ? 'wrap' : 'nowrap',
    position: 'relative',
    zIndex: 1
  },
  pageEyebrow: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: isMobile ? '5px 10px' : '6px 12px',
    borderRadius: 999,
    background: SOFT_BLUE,
    color: DEEP_BLUE,
    fontSize: isMobile ? 10 : 11,
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase'
  },
  pageNumber: {
    fontSize: isMobile ? 11 : 12,
    fontWeight: 800,
    color: '#6b85a6',
    letterSpacing: '0.08em'
  },
  pageTitle: {
    margin: 0,
    fontSize: isMobile ? 22 : isTablet ? 24 : 28,
    lineHeight: 1.08,
    color: '#0f172a',
    position: 'relative',
    zIndex: 1
  },
  pageBody: {
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: isMobile ? 2 : 6,
    scrollbarGutter: 'stable',
    display: 'grid',
    alignContent: 'start',
    position: 'relative',
    zIndex: 1
  },
  pageBodyCover: {
    overflow: 'hidden',
    paddingRight: 0,
    alignContent: 'stretch',
    height: '100%'
  },
  pageStack: {
    display: 'grid',
    gap: 16
  },
  pageStackCompact: {
    display: 'grid',
    gap: 12
  },
  coverLeadCard: {
    borderRadius: isMobile ? 20 : 24,
    padding: isMobile ? 16 : 20,
    background: `linear-gradient(135deg, ${DEEP_BLUE} 0%, ${BRAND_BLUE} 100%)`,
    color: '#eff6ff',
    boxShadow: '0 18px 40px rgba(22, 61, 107, 0.2)'
  },
  coverLeadLabel: {
    marginBottom: 10,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: '#bfdbfe'
  },
  coverLeadText: {
    margin: '0 0 14px',
    fontSize: isMobile ? 14 : 15,
    lineHeight: isMobile ? 1.68 : 1.75,
    color: '#f8fbff'
  },
  coverPageShell: {
    position: 'relative',
    minHeight: '100%',
    display: 'grid',
    gridTemplateRows: 'auto auto auto auto auto 1fr',
    alignContent: 'start',
    gap: isMobile ? 12 : 18,
    padding: isMobile ? '4px 2px 8px' : '8px 6px 12px',
    color: '#f8fbff'
  },
  coverPageGlow: {
    position: 'absolute',
    inset: 'auto -18% -18% auto',
    width: isMobile ? 200 : 320,
    height: isMobile ? 200 : 320,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 72%)',
    pointerEvents: 'none'
  },
  coverPageTopline: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifySelf: 'start',
    padding: '6px 12px',
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.12)',
    fontSize: isMobile ? 10 : 11,
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase'
  },
  coverPageBrand: {
    position: 'relative',
    fontSize: isMobile ? 16 : 18,
    fontWeight: 700,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'rgba(219, 234, 254, 0.88)'
  },
  coverPageTitle: {
    position: 'relative',
    margin: 0,
    fontSize: isMobile ? 'clamp(28px, 8.5vw, 38px)' : isTablet ? 'clamp(38px, 6vw, 52px)' : 'clamp(48px, 5vw, 66px)',
    lineHeight: 0.98,
    letterSpacing: '-0.03em',
    maxWidth: 760,
    textWrap: 'balance'
  },
  coverPageText: {
    position: 'relative',
    margin: 0,
    maxWidth: 700,
    fontSize: isMobile ? 14 : isTablet ? 15 : 17,
    lineHeight: 1.7,
    color: 'rgba(239, 246, 255, 0.94)'
  },
  coverPageEditionRow: {
    position: 'relative',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8
  },
  coverPageEditionChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: isMobile ? '7px 10px' : '8px 12px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.14)',
    border: '1px solid rgba(255,255,255,0.16)',
    color: '#eff6ff',
    fontSize: isMobile ? 11 : 12,
    fontWeight: 700,
    letterSpacing: '0.03em',
    backdropFilter: 'blur(10px)'
  },
  coverPageDivider: {
    position: 'relative',
    width: isMobile ? 84 : 128,
    height: 3,
    borderRadius: 999,
    background: 'linear-gradient(90deg, rgba(255,255,255,0.86) 0%, rgba(191,219,254,0.52) 100%)'
  },
  coverPageMetaGrid: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : 'repeat(3, minmax(0, 1fr))',
    gap: isMobile ? 8 : 10,
    alignSelf: 'end'
  },
  coverPageMetaCard: {
    borderRadius: isMobile ? 18 : 20,
    padding: isMobile ? '10px 11px' : isTablet ? '11px 12px' : '14px 14px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.14)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
    backdropFilter: 'blur(8px)'
  },
  coverPageMetaLabel: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'rgba(219, 234, 254, 0.84)',
    marginBottom: 6
  },
  coverPageMetaValue: {
    fontSize: isMobile ? 13 : isTablet ? 14 : 15,
    fontWeight: 700,
    color: '#f8fbff'
  },
  bookNavRow: {
    display: 'grid',
    gridTemplateColumns: isTablet ? '1fr 1fr' : 'auto 1fr auto',
    gap: isMobile ? 8 : 10,
    alignItems: 'center',
    marginTop: isMobile ? 8 : 12,
    padding: isMobile ? '8px 10px' : '10px 12px',
    borderRadius: isMobile ? 18 : 20,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(243,247,255,0.98) 100%)',
    border: '1px solid rgba(184, 204, 232, 0.92)',
    boxShadow: '0 12px 26px rgba(35, 101, 171, 0.08)',
    position: 'relative',
    width: 'min(920px, 100%)',
    maxWidth: 920,
    justifySelf: 'center',
    boxSizing: 'border-box',
    backdropFilter: 'blur(16px)',
    marginBottom: '35px'
  },
  bookNavButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    minHeight: 38,
    minWidth: isTablet ? 0 : 132,
    border: '1px solid rgba(16, 56, 96, 0.9)',
    background: `linear-gradient(135deg, ${DEEP_BLUE} 0%, ${BRAND_BLUE} 100%)`,
    color: '#f8fbff',
    borderRadius: 999,
    padding: isMobile ? '8px 10px' : '8px 14px',
    fontSize: isMobile ? 12 : 13,
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: '0 12px 22px rgba(22, 61, 107, 0.18), inset 0 1px 0 rgba(255,255,255,0.16)',
    letterSpacing: '0.01em',
    width: isTablet ? '100%' : 'auto'
  },
  bookNavButtonDisabled: {
    opacity: 0.45,
    background: 'linear-gradient(135deg, #7c94b1 0%, #93a9c4 100%)',
    borderColor: 'rgba(120, 143, 169, 0.95)',
    boxShadow: '0 10px 24px rgba(120, 143, 169, 0.16)',
    cursor: 'not-allowed'
  },
  bookNavHint: {
    textAlign: 'center',
    fontSize: isMobile ? 11 : 12,
    fontWeight: 700,
    color: '#557392',
    gridColumn: isTablet ? '1 / -1' : 'auto',
    order: isTablet ? 3 : 'initial',
    padding: isTablet ? '0 4px' : 0,
    lineHeight: 1.2,
    margin: isTablet ? '2px 0' : 0
  },
  bookNavArrow: {
    fontSize: 14,
    lineHeight: 1,
    fontWeight: 900,
    transform: 'translateY(-1px)'
  },
  bookNavButtonText: {
    whiteSpace: 'nowrap',
    lineHeight: 1
  },
  exportRoot: {
    position: 'absolute',
    left: '-20000px',
    top: 0,
    width: 794,
    padding: 0,
    background: '#ffffff',
    display: 'grid',
    gap: 0,
    pointerEvents: 'none'
  },
  exportPage: {
    width: 794,
    minHeight: 1123,
    boxSizing: 'border-box',
    overflow: 'hidden',
    borderRadius: 0,
    border: 'none',
    background: '#ffffff',
    padding: 28,
    boxShadow: 'none'
  },
  exportPageCover: {
    padding: 0,
    background: `linear-gradient(160deg, ${DEEP_BLUE} 0%, ${BRAND_BLUE} 55%, ${SKY_BLUE} 100%)`
  },
  exportPageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12
  },
  exportEyebrow: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: BRAND_BLUE
  },
  exportPageNumber: {
    fontSize: 12,
    fontWeight: 800,
    color: '#64748b'
  },
  exportPageTitle: {
    margin: '0 0 18px',
    fontSize: 28,
    lineHeight: 1.1,
    color: '#0f172a'
  },
  exportCoverShell: {
    minHeight: 1123,
    boxSizing: 'border-box',
    display: 'grid',
    alignContent: 'space-between',
    gap: 28,
    padding: '84px 72px 72px',
    color: '#f8fbff'
  },
  exportCoverOverline: {
    display: 'inline-flex',
    alignItems: 'center',
    justifySelf: 'start',
    padding: '8px 14px',
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.12)',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.18em',
    textTransform: 'uppercase'
  },
  exportCoverBrand: {
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '0.26em',
    textTransform: 'uppercase',
    color: 'rgba(219, 234, 254, 0.88)'
  },
  exportCoverTitle: {
    margin: 0,
    maxWidth: 560,
    fontSize: 62,
    lineHeight: 0.98,
    letterSpacing: '-0.04em'
  },
  exportCoverText: {
    margin: 0,
    maxWidth: 560,
    fontSize: 20,
    lineHeight: 1.7,
    color: 'rgba(239, 246, 255, 0.96)'
  },
  exportCoverDivider: {
    width: 160,
    height: 4,
    borderRadius: 999,
    background: 'linear-gradient(90deg, rgba(255,255,255,0.92) 0%, rgba(191,219,254,0.52) 100%)'
  },
  exportCoverFooter: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
    alignItems: 'stretch',
    marginTop: 'auto'
  },
  exportCoverFooterItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 64,
    padding: '12px 16px',
    borderRadius: 18,
    border: '1px solid rgba(255,255,255,0.14)',
    background: 'rgba(255,255,255,0.1)',
    color: '#f8fbff',
    fontSize: 16,
    fontWeight: 700,
    textAlign: 'center'
  },
  copy: {
    margin: '0 0 14px',
    fontSize: isMobile ? 14 : 15,
    lineHeight: isMobile ? 1.68 : 1.75,
    color: '#334155'
  },
  sectionCard: {
    borderRadius: isMobile ? 22 : 28,
    background: '#ffffff',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    boxShadow: '0 18px 44px rgba(15, 23, 42, 0.08)',
    padding: isMobile ? '20px 16px' : '28px clamp(18px, 3vw, 32px)'
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 16
  },
  grid3: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 16
  },
  twoCols: {
    display: 'grid',
    gridTemplateColumns: isTablet ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 18,
    alignItems: 'start'
  },
  infoCard: {
    borderRadius: isMobile ? 18 : 22,
    background: 'linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%)',
    border: '1px solid rgba(184, 204, 232, 0.92)',
    padding: isMobile ? 16 : 20,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.72)'
  },
  roleCard: {
    display: 'grid',
    gridTemplateColumns: isTablet ? '1fr' : 'minmax(240px, 0.9fr) 1.1fr',
    gap: isMobile ? 14 : 20,
    alignItems: 'start',
    borderRadius: isMobile ? 18 : 22,
    background: 'linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%)',
    border: '1px solid rgba(184, 204, 232, 0.92)',
    padding: isMobile ? 16 : 22
  },
  bulletCard: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start'
  },
  bulletDot: {
    width: 9,
    height: 9,
    marginTop: 7,
    borderRadius: '50%',
    background: BRAND_BLUE,
    flexShrink: 0,
    boxShadow: '0 0 0 6px rgba(35, 101, 171, 0.14)'
  },
  cardTitle: {
    margin: '0 0 10px',
    fontSize: isMobile ? 17 : 19,
    color: '#0f172a'
  },
  cardTag: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 11px',
    borderRadius: 999,
    background: SOFT_BLUE,
    color: DEEP_BLUE,
    fontSize: isMobile ? 11 : 12,
    fontWeight: 800,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: 14
  },
  credentialRow: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: isMobile ? 'column' : 'row',
    gap: 12,
    marginBottom: 10,
    paddingBottom: 10,
    borderBottom: '1px solid rgba(226, 232, 240, 0.9)',
    fontSize: isMobile ? 13 : 14,
    color: '#475569'
  },
  orderedList: {
    margin: 0,
    paddingLeft: 20,
    color: '#334155',
    lineHeight: 1.8,
    fontSize: isMobile ? 14 : 15
  },
  list: {
    margin: 0,
    paddingLeft: 20,
    color: '#334155',
    lineHeight: 1.8,
    fontSize: isMobile ? 14 : 15
  },
  highlightPanel: {
    borderRadius: isMobile ? 18 : 22,
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    color: '#e2e8f0',
    padding: isMobile ? 16 : 22,
    boxShadow: '0 18px 36px rgba(15, 23, 42, 0.18)'
  },
  highlightPanelAlt: {
    borderRadius: isMobile ? 18 : 22,
    background: `linear-gradient(135deg, ${SOFT_BLUE} 0%, #c9dcf7 100%)`,
    color: '#1d3d67',
    padding: isMobile ? 16 : 22,
    border: '1px solid rgba(35, 101, 171, 0.18)'
  },
  highlightTitle: {
    marginBottom: 10,
    fontSize: isMobile ? 15 : 16,
    fontWeight: 800,
    color: 'inherit'
  },
  highlightPanelText: {
    margin: '0 0 14px',
    fontSize: isMobile ? 14 : 15,
    lineHeight: isMobile ? 1.68 : 1.75,
    color: '#e2e8f0'
  },
  flowCard: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '56px 1fr',
    gap: isMobile ? 12 : 16,
    alignItems: 'start',
    padding: isMobile ? 16 : 18,
    borderRadius: isMobile ? 18 : 22,
    border: '1px solid rgba(184, 204, 232, 0.8)',
    background: 'linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%)'
  },
  flowIndex: {
    width: isMobile ? 48 : 56,
    height: isMobile ? 48 : 56,
    borderRadius: 18,
    background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${DEEP_BLUE} 100%)`,
    color: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: isMobile ? 18 : 22,
    fontWeight: 900,
    boxShadow: '0 18px 30px rgba(35, 101, 171, 0.2)'
  },
  noteRow: {
    display: 'grid',
    gridTemplateColumns: '10px 1fr',
    gap: 12,
    alignItems: 'start',
    padding: isMobile ? '12px 14px' : '14px 16px',
    borderRadius: isMobile ? 16 : 18,
    background: '#f6f9ff',
    border: '1px solid rgba(214, 226, 243, 0.92)'
  },
  noteRowSoft: {
    display: 'grid',
    gridTemplateColumns: '10px 1fr',
    gap: 12,
    alignItems: 'start',
    padding: isMobile ? '11px 12px' : '12px 14px',
    borderRadius: isMobile ? 14 : 16,
    background: '#f6f9ff',
    border: '1px solid rgba(214, 226, 243, 0.88)'
  },
  noteAccent: {
    width: 10,
    height: 10,
    marginTop: 8,
    borderRadius: '50%',
    background: BRAND_BLUE
  },
  noteAccentSoft: {
    width: 10,
    height: 10,
    marginTop: 8,
    borderRadius: '50%',
    background: SKY_BLUE
  },
  lifecycleCard: {
    display: 'grid',
    gridTemplateColumns: isTablet ? '1fr' : '220px 1fr',
    gap: isMobile ? 14 : 18,
    alignItems: 'start',
    borderRadius: isMobile ? 18 : 22,
    border: '1px solid rgba(184, 204, 232, 0.82)',
    background: 'linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%)',
    padding: isMobile ? 16 : 20
  },
  lifecycleMeta: {
    borderRadius: isMobile ? 16 : 18,
    background: `linear-gradient(135deg, ${DEEP_BLUE} 0%, ${BRAND_BLUE} 100%)`,
    color: '#f8fafc',
    padding: isMobile ? 14 : 18,
    boxShadow: '0 16px 28px rgba(35, 101, 171, 0.2)'
  },
  lifecycleOwnerLabel: {
    fontSize: isMobile ? 10 : 11,
    fontWeight: 800,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    opacity: 0.86,
    marginBottom: 8
  },
  lifecycleOwnerValue: {
    fontSize: isMobile ? 17 : 20,
    fontWeight: 800,
    lineHeight: 1.25
  },
  faqCard: {
    borderRadius: isMobile ? 18 : 20,
    padding: isMobile ? 16 : 20,
    background: 'linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%)',
    border: '1px solid rgba(214, 226, 243, 0.95)'
  }
  };
}
